import type { FC } from 'react';
import type { RequestFailureKind } from 'src/utils/requestError';

import React from 'react';
import { WifiSlash, CarSimple, HourglassHigh, WarningCircle } from '@phosphor-icons/react';

import { EmptyState } from './EmptyState';

interface RequestFailureStateProps {
  kind: RequestFailureKind;
  /** What was being fetched, for the 404 copy: "This listing" / "This car". */
  subject?: string;
  /** Where "not found" leads back to. */
  onBack?: () => void;
  backLabel?: string;
  /** Refetch — for everything that may work the second time. */
  onRetry?: () => void;
  /** `h1` on a screen that shows this state instead of its own title (see `EmptyState`). */
  titleAs?: 'h1';
}

const ICON_SIZE = 48;

/**
 * The failure copy, by kind. One component so the four screens that fetch something public say
 * the same thing in the same words when it goes wrong — and so a "not found" always has a way
 * back, since the listing screens hide the tab bar.
 */
export const RequestFailureState: FC<RequestFailureStateProps> = (props) => {
  const { kind, subject = 'This listing', onBack, backLabel = 'Back to cars', onRetry, titleAs } = props;
  const retry = onRetry ? { label: 'Try again', onClick: onRetry } : undefined;

  switch (kind) {
    case 'not-found':
      return (
        <EmptyState
          titleAs={titleAs}
          icon={<CarSimple size={ICON_SIZE} />}
          title={`${subject} is no longer available`}
          description="It was sold, booked out, or removed by the dealer."
          // A feed has no "back"; if its endpoint ever answers 404 the way out is to ask again.
          action={onBack ? { label: backLabel, onClick: onBack } : retry}
        />
      );
    case 'offline':
      return (
        <EmptyState
          titleAs={titleAs}
          tone="warning"
          icon={<WifiSlash size={ICON_SIZE} />}
          title="No connection"
          description="Check your network. The cars will load as soon as it is back."
          action={retry}
        />
      );
    case 'timeout':
      return (
        <EmptyState
          titleAs={titleAs}
          tone="warning"
          icon={<HourglassHigh size={ICON_SIZE} />}
          title="This is taking longer than usual"
          description="The server did not answer in time."
          action={retry}
        />
      );
    default:
      return (
        <EmptyState
          titleAs={titleAs}
          tone="danger"
          icon={<WarningCircle size={ICON_SIZE} />}
          title="We could not load this"
          description="An error on our side. Please try again in a moment."
          action={retry}
        />
      );
  }
};
